import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAccessToken, getNativeWebview } from '../react-queries/queries';
import { processApiResponse } from '../common/errorMessage';
import { getBookeventApplicants } from '../api/BookeventApi';
import { ApplicantStatus } from '../common/enums';
import LoadingOverlay from '../components/LoadingOverlay';
import { launchUrl } from '../api/native/NativeApis';

const Container = styled.div`
  padding: 0;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px 20px 0 20px;
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #222;
  margin: 0;
`;

const ExcelButton = styled.button`
  padding: 8px 16px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: #218838;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: hidden;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin: 0 20px 20px 20px;
  display: flex;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 600px;
  border: 1px solid #dee2e6;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
  display: table-header-group;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f8f9fa;
  }
  
  &:hover {
    background: #e3f2fd;
  }
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  color: #222;
  border-bottom: 2px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  white-space: nowrap;
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
  
  &:last-child {
    border-right: none;
  }
`;

const TableBody = styled.tbody`
  display: table-row-group;
`;

const TableCell = styled.td`
  padding: 12px 8px;
  border-bottom: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  font-size: 14px;
  color: #333;
  word-break: break-all;
  
  &:last-child {
    border-right: none;
  }
`;

const LinkUrl = styled.a`
  color: #007bff;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:visited {
    color: #6f42c1;
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 14px;
`;

const formatMailingAddress = (applicant) => {
  const parts = [
    applicant?.mailingPostalCode,
    applicant?.mailingAddr,
    applicant?.mailingAddrDetail,
  ].filter((part) => part && String(part).trim());
  return parts.join(' ');
};

export default function ReviewMoimMissiondoneLinkPage({ bookEventId }) {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 지원자 목록 로드
  const loadApplicants = async () => {
    if (!bookEventId || loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { accessToken } = await getAccessToken();
      const response = await getBookeventApplicants(
        accessToken || '', 
        bookEventId, 
        null, 
        null, 
        null, 
        ApplicantStatus.ACCEPTED, 
        0, 
        500
      );
      
      const processedResponse = processApiResponse(response);
      
      if (processedResponse.success) {
        setApplicants(processedResponse.data || []);
      } else {
        setError(processedResponse.message || '지원자 목록을 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('Error loading applicants:', error);
      setError('지원자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 엑셀 다운로드
  const downloadExcel = () => {
    if (applicants.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    // 모든 지원자의 missiondoneLinks에서 고유한 typeName들을 수집
    const allTypeNames = new Set();
    applicants.forEach(applicant => {
      if (applicant.missiondoneLinks) {
        applicant.missiondoneLinks.forEach(link => {
          if (link.missiondoneType?.typeName) {
            allTypeNames.add(link.missiondoneType.typeName);
          }
        });
      }
    });

    const typeNames = Array.from(allTypeNames);

    // CSV 헤더 생성
    const headers = ['이름', '아이디', '이메일', '전화번호', '주소', ...typeNames];
    
    // CSV 데이터 생성
    const csvData = applicants.map(applicant => {
      const row = [
        applicant.name || '',
        applicant.loginid || '',
        applicant.email || '',
        applicant.handphoneNum || '',
        formatMailingAddress(applicant)
      ];

      // 각 타입별로 링크 URL 추가
      typeNames.forEach(typeName => {
        const link = applicant.missiondoneLinks?.find(
          link => link.missiondoneType?.typeName === typeName
        );
        row.push(link?.linkUrl || '');
      });

      return row;
    });

    // CSV 문자열 생성
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // BOM 추가 (한글 깨짐 방지)
    const BOM = '\uFEFF';
    const fileName = `missiondone_${new Date().toISOString().split('T')[0]}.csv`;
    const blob = new Blob([BOM + csvContent], { type: 'text/csv' });

    // 웹뷰: 클라이언트 생성 파일은 http URL이 아니라 launchUrl(Linking)로 저장 불가.
    // 앱 WebView의 blob 다운로드(__BLOB_DOWNLOAD__)로 저장한다.
    if (getNativeWebview()) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result !== 'string') {
          return;
        }
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: '__BLOB_DOWNLOAD__',
            filename: fileName,
            mimeType: 'text/csv',
            data: reader.result,
          }));
          return;
        }
        // ReactNativeWebView 없으면 data URL로 launchUrl 시도
        launchUrl(reader.result);
      };
      reader.readAsDataURL(blob);
      return;
    }

    // 브라우저 다운로드
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadApplicants();
  }, [bookEventId]);

  if (loading) {
    return <LoadingOverlay />;
  }

  if (error) {
    return (
      <Container>
        <EmptyMessage>{error}</EmptyMessage>
      </Container>
    );
  }

  if (applicants.length === 0) {
    return (
      <Container>
        <Header>
          <Title>활동내역 현황</Title>
        </Header>
        <EmptyMessage>선정된 지원자가 없습니다.</EmptyMessage>
      </Container>
    );
  }

  // 모든 지원자의 missiondoneLinks에서 고유한 typeName들을 수집
  const allTypeNames = new Set();
  applicants.forEach(applicant => {
    if (applicant.missiondoneLinks) {
      applicant.missiondoneLinks.forEach(link => {
        if (link.missiondoneType?.typeName) {
          allTypeNames.add(link.missiondoneType.typeName);
        }
      });
    }
  });

  const typeNames = Array.from(allTypeNames).sort();

  return (
    <Container>
      <Header>
        <Title></Title>
        <ExcelButton onClick={downloadExcel}>
          엑셀다운로드
        </ExcelButton>
      </Header>

      <TableContainer>
        <TableWrapper>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>이름</TableHeaderCell>
                <TableHeaderCell>아이디</TableHeaderCell>
                <TableHeaderCell>이메일</TableHeaderCell>
                <TableHeaderCell>전화번호</TableHeaderCell>
                <TableHeaderCell>메일수신 주소</TableHeaderCell>
                {typeNames.map((typeName, index) => (
                  <TableHeaderCell key={index}>{typeName}</TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {applicants.map((applicant, index) => (
                <TableRow key={index}>
                  <TableCell>{applicant.name || ''}</TableCell>
                  <TableCell>{applicant.loginid || ''}</TableCell>
                  <TableCell>{applicant.email || ''}</TableCell>
                  <TableCell>{applicant.handphoneNum || ''}</TableCell>
                  <TableCell>{formatMailingAddress(applicant)}</TableCell>
                  {typeNames.map((typeName, typeIndex) => {
                    const link = applicant.missiondoneLinks?.find(
                      link => link.missiondoneType?.typeName === typeName
                    );
                    return (
                      <TableCell key={typeIndex}>
                        {link?.linkUrl ? (
                          <LinkUrl 
                            href={link.linkUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            {link.linkUrl}
                          </LinkUrl>
                        ) : ''}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrapper>
      </TableContainer>
    </Container>
  );
}
